<template>
    <ActionStatusModal
        v-if="sendTestEmailStatus==='success'"
        additional-class-name="Send"
        message="Test Email Sent"
        type="success"
        open
    />
    <SendTestEmailModal
        v-else
        open
        :type="type"
        :submitting="submitting"
        :audience-choice="audienceChoice"
        @close="$emit('abort')"
        @submit="onSubmit"
    />
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import {
        NotificationAudience,
        NotificationType,
        PRODUCER_NOTIFICATION_TYPES,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration';
    import {
        VerificationRequestType,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration/VerificationRequest.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ActionStatusModal
        from '@/modules/notifications-configuration/components/ActionStatusModal/ActionStatusModal.vue';
    import SendTestEmailModal
        from '@/modules/notifications-configuration/components/SendTestEmailModal/SendTestEmailModal.vue';
    import { useNotificationsConfigurationStore } from '@/modules/notifications-configuration/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: {
            ActionStatusModal,
            SendTestEmailModal,
        },
        emits: [ 'abort', 'finish' ],
    })
    export default class SendTestEmailProcedure extends Vue {
        private get notificationsConfigurationStore() {
            return useNotificationsConfigurationStore();
        }

        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String })
        private rpName!: string;

        @Prop({ type: String })
        private type!: VerificationRequestType;

        private get sendTestEmailStatus(): OperationStatus {
            return this.notificationsConfigurationStore.sendTestEmailStatus?.[this.rpName]
                || OperationStatus.uninitialized;
        }

        private get submitting(): boolean {
            return this.sendTestEmailStatus === OperationStatus.loading;
        }

        private get userEmail(): string {
            return useUserStore().data?.email || '';
        }

        // With producer flow on, the test email for a type producers receive can be sent as either
        // audience would get it.
        private get audienceChoice(): boolean {
            const producerFlowEnabled = (this as any).$rp?.currentDetails?.producerFlowEnabled || false;
            return producerFlowEnabled && PRODUCER_NOTIFICATION_TYPES.includes(this.type as string as NotificationType);
        }

        private async onSubmit(audience?: NotificationAudience): Promise<void> {
            await this.notificationsConfigurationStore.sendTestEmail({
                rpName: this.rpName,
                type: this.type,
                to: this.userEmail,
                cc: [],
                audience,
            });

            if (this.sendTestEmailStatus === OperationStatus.success) {
                setTimeout(() => {
                    this.$emit('finish');
                    this.notificationsConfigurationStore.clearSendTestEmailStatus(this.rpName);
                }, 2000);
            } else if (this.sendTestEmailStatus === OperationStatus.error) {
                await useSnackbarStore().displaySnackbar({
                    message: 'Something went wrong, please try again later.',
                    success: false,
                });
            }
        }
    }
</script>
